import {fetchWithAuth, httpUtil} from '$lib/utils/http/httpUtil';
import {showToast} from "$lib/helpers/toastrHelper";
import type {BaseListResponse} from "$lib/models/types";
import type {ApiResult} from "$lib/types";
import {extractErrorMessage} from "$lib";
import {writable} from 'svelte/store';

const baseUrl = '/api/localisations';

// --- Selection state (reactive) ---
export type LocalisationState = {
  selectedClientId: number | undefined;
  selectedLocale: LocaleKey | undefined;
  localeKeys: LocaleKey[];
  localisationSearchResults: LocalisationItem[];
  localisationOriginalSearchResults: LocalisationItem[];  // snapshot at fetch time; diff against searchResults to detect changes
}

export const localisationStore = writable<LocalisationState>({
  selectedClientId: undefined,
  selectedLocale: undefined,
  localeKeys: [],
  localisationSearchResults: [],
  localisationOriginalSearchResults: [],
});

export type LocaleKey = {
  id: string;        // BCP-47 culture name, e.g. "en-AU"
  location: number;  // LocationEnum
  language: number;  // LanguageEnum
}

export type LocalisationItem = {
  key: string;
  value: string;
}

export type SearchLocalisationsResponse = {
  items: LocalisationItem[];
  pageNumber: number;
  hasNextPage: boolean;
}

export type SearchLocalisationsRequest = {
  clientId?: number;
  location?: number;  // LocationEnum, nullable upstream
  language: number;   // LanguageEnum
  pageNumber: number;
  pageSize: number;
}

export type UpsertLocalisationForCultureRequest = {
  location: number;
  language: number;
  entries: LocalisationItem[];
}

export type UpsertLocalisationForClientRequest = {
  clientId: number;
  entries: LocalisationItem[];
}

export type LocalisationKeyDescription = {
  key: string;
  description: string;
}

export type GetLocalisationKeyDescriptionsResponse = {
  keys: LocalisationKeyDescription[];
}

// --- Cached (enum-derived, immutable per build) ---
let localeKeysCache: Promise<LocaleKey[]> | null = null;
let keyDescriptionsCache: Promise<Record<string, string>> | null = null;

export async function getKeyDescriptions(): Promise<Record<string, string>> {
  try {
    return await (keyDescriptionsCache ??= httpUtil
      .get<GetLocalisationKeyDescriptionsResponse>(`${baseUrl}/key-descriptions`)
      .then(unwrap)
      .then(r => Object.fromEntries((r.keys ?? []).map(k => [k.key, k.description]))));
  } catch (err) {
    keyDescriptionsCache = null;
    throw err;
  }
}

export async function getLocaleKeys(): Promise<LocaleKey[]> {
  try {
    const keys = await (localeKeysCache ??= httpUtil.get<LocaleKey[]>(`${baseUrl}/locale-keys`).then(unwrap));
    localisationStore.update(s => {
      s.localeKeys = keys;
      return s;
    });
    return keys;
  } catch (err) {
    localeKeysCache = null;
    throw err;
  }
}

export function clearLocaleKeysCache(): void {
  localeKeysCache = null;
}

function resetLocalisationData(): void {
  localisationStore.update(s => {
    s.localisationSearchResults = [];
    s.localisationOriginalSearchResults = [];
    return s;
  });
}

export async function searchLocalisations(request_: Pick<SearchLocalisationsRequest, 'language' | 'location' | 'clientId'>): Promise<BaseListResponse> {
  resetLocalisationData();

  const qs = new URLSearchParams({pageNumber: '1', pageSize: '1000'});
  if (request_.clientId !== undefined) qs.set('clientId', String(request_.clientId));
  if (request_.location !== undefined) qs.set('location', String(request_.location));
  if (request_.language !== undefined) qs.set('language', String(request_.language));

  // Datatables handler can't tolerate throws — convert to an empty result.
  try {
    const data = await unwrap(await httpUtil.get<SearchLocalisationsResponse>(`${baseUrl}/search?${qs}`));
    localisationStore.update(s => {
      s.localisationSearchResults = data.items;
      s.localisationOriginalSearchResults = structuredClone(data.items);
      return s;
    });
    return {total: data.items.length, data: data.items as unknown as BaseListResponse['data']};
  } catch {
    return {total: 0, data: []};
  }
}

export function upsertForCulture(body: UpsertLocalisationForCultureRequest): Promise<void> {
  return postVoid(`${baseUrl}/upsert-culture`, body);
}

export function upsertForClient(body: UpsertLocalisationForClientRequest): Promise<void> {
  return postVoid(`${baseUrl}/upsert-client`, body);
}

// --- helpers ---
function unwrap<T>(result: ApiResult<T>): T {
  if (result.success) return result.data;
  const message = result.errors?.join('; ') ?? 'Request failed';
  showToast({title: 'Error', message, type: 'error'});
  throw new Error(message);
}

async function postVoid(url: string, body: unknown): Promise<void> {
  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const message = await extractErrorMessage(response, `POST ${url} failed (HTTP ${response.status})`);
    showToast({title: 'Error', message, type: 'error'});
    throw new Error(message);
  }
}

// Alii.Core.Contracts.Common.Enums. If new values are added there and not mirrored here,
// the display falls back to the numeric value.
const languageNames: Record<number, string> = {0: 'English'};
const locationNames: Record<number, string> = {
  0: 'Australia',
  1: 'New Zealand',
  2: 'United Kingdom',
  3: 'Canada',
  4: "United States",
  5: "Singapore"
};

export function getLanguageName(language: number | undefined): string {
  if (language === undefined || language == null) return '';
  return languageNames[language] ?? language.toString();
}

export function getLocationName(location: number | undefined): string {
  if (location === undefined || location == null) return '';
  return locationNames[location] ?? location.toString();
}
